import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { trackEvent } from '@Services/analytics/tracking.service';

export const WEBSITE_OPTIONS = {
  WEBSITE: 'website',
  ONLINE_PROFILE: 'online_profile',
  NO_PUBLIC_SITE: 'no_public_site',
} as const;

export type WebsiteOption = (typeof WEBSITE_OPTIONS)[keyof typeof WEBSITE_OPTIONS];

export const NO_PUBLIC_SITE_ANSWER = 'No public site yet';
const minLengthUrl = 5;
const maxLengthUrl = 200;

const ALL_WEBSITE_QUESTION_IDS = [
  EddEmbeddedFields.EDD_WEBSITE_URL,
  EddEmbeddedFields.EDD_ONLINE_PROFILE_URL,
  EddEmbeddedFields.EDD_NO_PUBLIC_SITE,
] as const;

const OPTION_TO_QUESTION_ID: Record<WebsiteOption, EddEmbeddedFields> = {
  [WEBSITE_OPTIONS.WEBSITE]: EddEmbeddedFields.EDD_WEBSITE_URL,
  [WEBSITE_OPTIONS.ONLINE_PROFILE]: EddEmbeddedFields.EDD_ONLINE_PROFILE_URL,
  [WEBSITE_OPTIONS.NO_PUBLIC_SITE]: EddEmbeddedFields.EDD_NO_PUBLIC_SITE,
};

const deriveDefaults = () => {
  const websiteAnswer = getDefaultValueByQuestionId(EddEmbeddedFields.EDD_WEBSITE_URL) as
    string | undefined;
  if (websiteAnswer) {
    return { option: WEBSITE_OPTIONS.WEBSITE, url: websiteAnswer };
  }

  const profileAnswer = getDefaultValueByQuestionId(EddEmbeddedFields.EDD_ONLINE_PROFILE_URL) as
    string | undefined;
  if (profileAnswer) {
    return { option: WEBSITE_OPTIONS.ONLINE_PROFILE, url: profileAnswer };
  }

  const noSiteAnswer = getDefaultValueByQuestionId(EddEmbeddedFields.EDD_NO_PUBLIC_SITE) as
    string | undefined;
  if (noSiteAnswer) {
    return { option: WEBSITE_OPTIONS.NO_PUBLIC_SITE, url: '' };
  }

  return { option: '', url: '' };
};

export const useWebsiteSocialLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');

  const { option: defaultOption, url: defaultUrl } = deriveDefaults();

  const formMethods = useFormHook<{
    [EddEmbeddedFields.EDD_WEBSITE_OPTION]: string;
    [EddEmbeddedFields.EDD_WEBSITE_URL]: string;
  }>({
    schemaTitle: 'Website and Social Media',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_WEBSITE_OPTION]: defaultOption,
      [EddEmbeddedFields.EDD_WEBSITE_URL]: defaultUrl,
    },
    formSchema: z
      .object({
        [EddEmbeddedFields.EDD_WEBSITE_OPTION]: z
          .string()
          .min(1, { message: t('organic.websiteSocial.validation.selectOption') }),
        [EddEmbeddedFields.EDD_WEBSITE_URL]: z.string(),
      })
      .superRefine((data, ctx) => {
        const option = data[EddEmbeddedFields.EDD_WEBSITE_OPTION];
        const url = data[EddEmbeddedFields.EDD_WEBSITE_URL];
        if (option && option !== WEBSITE_OPTIONS.NO_PUBLIC_SITE) {
          if (!url || url.trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('organic.websiteSocial.validation.urlRequired'),
              path: [EddEmbeddedFields.EDD_WEBSITE_URL],
            });
          } else if (url.trim().length < minLengthUrl) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('organic.websiteSocial.validation.minLengthUrl', {
                minLength: minLengthUrl,
              }),
              path: [EddEmbeddedFields.EDD_WEBSITE_URL],
            });
          } else if (url.trim().length > maxLengthUrl) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('organic.websiteSocial.validation.maxLengthUrl', {
                maxLength: maxLengthUrl,
              }),
              path: [EddEmbeddedFields.EDD_WEBSITE_URL],
            });
          }
        }
      }),
  });

  const handleSubmit = async () => {
    const option = formMethods.getValues(EddEmbeddedFields.EDD_WEBSITE_OPTION) as WebsiteOption;
    const url = formMethods.getValues(EddEmbeddedFields.EDD_WEBSITE_URL);
    const questionId = OPTION_TO_QUESTION_ID[option];
    const answer = option === WEBSITE_OPTIONS.NO_PUBLIC_SITE ? NO_PUBLIC_SITE_ANSWER : url;

    const { updateEddEmbeddedQuestions, getEddEmbeddedQuestions } = useBoundStore.getState();
    const staleIds = ALL_WEBSITE_QUESTION_IDS.filter((id) => id !== questionId);
    const normalizeId = (id: string) => id.toLowerCase().replace(/_/g, '-');
    const filtered = getEddEmbeddedQuestions().filter(
      (q) => !staleIds.some((staleId) => normalizeId(staleId) === normalizeId(q.questionId))
    );
    updateEddEmbeddedQuestions(filtered);

    saveEddQuestionToLocalStore({
      question: t('organic.websiteSocial.title'),
      questionId,
      answer,
    });

    const {
      customer,
      onboardingSlice,
      getEddEmbeddedQuestions: getLatestQuestions,
    } = useBoundStore.getState();
    const entityType = customer.customerData.customerBusinessData.mainIncomeSource || '';
    trackEvent('act-on-website-in-edd', {
      selected_option: option,
      entity_type: entityType,
      is_partner_flow: onboardingSlice.isPartnerFlow,
    });

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getLatestQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_WEBSITE_SOCIAL);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
